/**
 * Single source of truth for "is a professional listing publicly visible".
 *
 * Two axes gate a listing:
 *  1. `visible = false` — the owner deliberately hid it.
 *  2. subscription state:
 *     - `cancelled` / `suspended` → always gated.
 *     - `past_due` → gated only after a 10-day grace window (a late/failed
 *       payment shouldn't instantly hide a pro and cost them business).
 *       Grace is measured from `subscription_expires_at` (when the paid period
 *       lapsed); a `past_due` row with no expiry date is left visible (lenient,
 *       and it's a data anomaly we don't want to punish).
 *
 * Owners always see their OWN listing (with a status banner) — gating applies
 * to the public only. List queries exclude gated rows at the DB level (so
 * pagination stays correct); the single-row slug lookup fetches the row and
 * lets the page decide (full / paused / owner-view) via `isListingPublic`.
 *
 * NOTE: `past_due` auto-transition by date needs the subscription-expiry cron
 * (separate P1 item) to be fully reliable — until then this gates explicitly
 * set states + the date-based past_due grace.
 */

/**
 * Pricing tiers, in the order the product treats them. Mirrors the
 * `professionals_plan_check` CHECK constraint added in
 * `20260830140000_pricing_plan_column` — widening one without the other breaks
 * at write time, not compile time.
 */
export type PlanTier = "free" | "premium" | "partner" | "founding";

/**
 * Is this professional on a PAID tier?
 *
 * One definition, every surface. Written here rather than at each consumer for
 * the same reason `cityFilter` / `languageFilter` exist in `slots.ts`: the
 * alternative is `plan !== "free"` hand-written at the ranking function, three
 * search queries, the badge helper and the chat card mapper, where it drifts
 * the first time someone adds a tier.
 *
 * An ALLOWLIST, deliberately, not `plan !== "free"`. The first version was the
 * negative test and a unit test caught it handing paid perks to the empty
 * string — and `?? ""` is the dominant idiom in the row mappers, so one
 * `plan: row.plan ?? ""` would have silently upgraded every such row. With an
 * allowlist, anything unrecognised (null, undefined, "", a typo, a tier added
 * to the DB CHECK but not here) reads as FREE, which is the safe direction:
 * an unclassifiable row gets no priority placement, no badge, no published
 * contact details, rather than being handed all three.
 *
 * The cost is that adding a paid tier means editing this set as well as the
 * CHECK constraint. That is the intended friction — a new tier should be a
 * decision in two places, not an accident in one.
 *
 * The database has the same predicate as a generated column, `is_paid`, used by
 * the DB-paginated sorts that never reach `rankRecommended`. Keep them in step.
 */
const PAID_PLANS: ReadonlySet<string> = new Set<PlanTier>(["premium", "partner", "founding"]);

export function isPaidPlan(plan: string | null | undefined): boolean {
  return plan != null && PAID_PLANS.has(plan);
}

/**
 * Should this professional's listing show the green VERIFIED badge publicly?
 *
 * `verified` is still what it always was: an admin checked their identity and
 * business registration at approval. What changed is that DISPLAYING it is now
 * a paid perk, so the two must be asked as one question at every public
 * surface — hence one helper rather than `verified && plan !== "free"` written
 * out five times.
 *
 * ⚠ THIS CHANGES WHAT THE BADGE MEANS TO A READER. Before, an unbadged listing
 * was one we had not checked. After, it may simply be one that did not pay. We
 * still verify EVERY professional at approval. `/terms` §3 and `/how-it-works`
 * describe the verification promise, so they carry a sentence saying the badge
 * marks a paid listing — otherwise the pages contradict the directory.
 *
 * OWNER AND ADMIN SURFACES DO NOT USE THIS. A pro's own dashboard and the admin
 * accounts panel show the true `verified` state, because there the question is
 * "did we check this account", not "what does the public see". Gating those too
 * would tell a verified pro they are unverified, which is false.
 */
export function showsVerifiedBadge(
  pro: { verified?: boolean | null; plan?: string | null },
): boolean {
  return Boolean(pro.verified) && isPaidPlan(pro.plan);
}

/**
 * Should a viewer be shown this professional's DIRECT contact details
 * (email / phone / WhatsApp / Zalo / Facebook / website)?
 *
 * Publishing them is a Premium / Founding Member perk (`publishesDirectContact`).
 * A free or PARTNER listing is reachable through the contact form only (a
 * partner's website still shows, see `publishesWebsite`), even for a signed-in
 * visitor, which is the axis the plans add on top of the
 * pre-existing anon/signed-in split.
 *
 * Two exemptions, both about "whose data is this":
 *  - the OWNER, who must see their own details on their own public page;
 *  - ADMINS, because the admin panel uses that page as its preview surface.
 *
 * Extracted as a pure function so it is testable. The caller
 * (`getProfessionalBySlug`) resolves the viewer and then nulls the fields in
 * its MAPPING — never in JSX, and never by trimming the select, because the
 * column list is interpolated per-viewer before the row's plan is known.
 */
export function hidesDirectContact(args: {
  plan: string | null | undefined;
  viewerIsOwner: boolean;
  viewerIsAdmin: boolean;
}): boolean {
  if (args.viewerIsOwner || args.viewerIsAdmin) return false;
  return !publishesDirectContact(args.plan);
}

/**
 * The plans whose listing publishes direct contact details: Premium and
 * Founding Member. An ALLOWLIST, like `isPaidPlan`, so an unknown or empty plan
 * publishes nothing.
 *
 * ⛔ NOT `isPaidPlan`. A PARTNER pays on the work we introduce (owner,
 * 2026-10-09: "hide direct contact details so clients contact them through the
 * form"), so their clients must reach them through the contact form, where the
 * introduction is recorded; a phone number on the page would route the first
 * contact around the commission. Partners keep every other paid perk.
 */
export const DIRECT_CONTACT_PLANS = ["premium", "founding"] as const;

export function publishesDirectContact(plan: string | null | undefined): boolean {
  return (DIRECT_CONTACT_PLANS as readonly string[]).includes(plan ?? "");
}

/**
 * A partner's WEBSITE still shows (owner, 2026-10-09: "we can still display
 * partner's websites"): it presents the firm rather than opening a private
 * line to it. So the website follows the paid tiers and the rest of the direct
 * contact follows `publishesDirectContact`.
 */
export function publishesWebsite(plan: string | null | undefined): boolean {
  return isPaidPlan(plan);
}

/**
 * Client inquiries a FREE listing may receive, for the LIFE of the account.
 *
 * Mirrors the DB trigger in `20260830210100_free_inquiry_cap_enforce`. The
 * DATABASE is the boundary — `submitContactAction` runs on the user client, so
 * a direct PostgREST insert would bypass anything written in the action. This
 * constant exists so the UI can avoid showing a form that will be refused, and
 * changing it here alone changes NOTHING about enforcement.
 */
export const FREE_INQUIRY_LIMIT = 3;

/**
 * Has this free listing spent its lifetime inquiries?
 *
 * Paid tiers are never exhausted, so this is false for them by construction —
 * which is what makes it safe as a ranking tier: it partitions only WITHIN the
 * free block and is a no-op above it.
 */
export function hasSpentFreeInquiries(
  pro: { plan?: string | null; inquiries_received?: number | null },
): boolean {
  if (isPaidPlan(pro.plan)) return false;
  return (pro.inquiries_received ?? 0) >= FREE_INQUIRY_LIMIT;
}

/** How many service packages a listing shows publicly, by tier. */
export const FREE_PUBLIC_PACKAGE_LIMIT = 1;

/**
 * The packages the PUBLIC sees for this listing.
 *
 * A free listing publishes one package; paid tiers publish all of them.
 *
 * ⚠ CAPS ARE VISIBILITY, NOT WRITE LIMITS (pricing-tiers-spec.md §5). Nothing
 * is deleted and nothing is blocked: a free pro keeps every package they
 * authored, and paying again restores them instantly with no re-authoring.
 * That is why this is a projection in the query layer and NOT a CHECK
 * constraint — a constraint would forbid exactly the storage we want to keep.
 *
 * ⚠ Do not let that reasoning travel to `plan` / `verified` / `founder`. The
 * test is: COULD A USER GRANT THEMSELVES SOMETHING? For caps, no — the data is
 * already theirs and the "exploit" is reading their own row. For tier, yes,
 * which is why that one is enforced in Postgres.
 *
 * Callers must pass packages ALREADY ORDERED by `sort_order`; the pro chooses
 * which package is the public one by reordering them in the dashboard.
 *
 * ⚠ SLICE BEFORE DERIVING ANY PRICE. `lowestPaidPrice` feeds the card's
 * "from" line, the chat card, AND the chat's budget HARD FILTER — computing any
 * of them over the full list advertises, or matches on, a price the profile
 * does not show.
 */
export function publicPackages<T>(
  plan: string | null | undefined,
  packages: readonly T[] | null | undefined,
): T[] {
  const all = packages ? [...packages] : [];
  return isPaidPlan(plan) ? all : all.slice(0, FREE_PUBLIC_PACKAGE_LIMIT);
}

/**
 * Categories a listing is PUBLISHED under, counting the primary (owner,
 * 2026-09-19; was 1 and 3). A pro may STORE up to `MAX_SECONDARY_CATEGORIES`
 * whatever their plan; the free tier publishes the first of them.
 */
export const FREE_LISTED_CATEGORY_LIMIT = 2;
export const PAID_LISTED_CATEGORY_LIMIT = 4;
/** Secondaries a pro may store (the DB CHECK on `secondary_categories`). */
export const MAX_SECONDARY_CATEGORIES = PAID_LISTED_CATEGORY_LIMIT - 1;

/** Secondaries this plan publishes, and so the editors' pick limit. */
export function secondaryCategoryLimit(plan: string | null | undefined): number {
  return (isPaidPlan(plan) ? PAID_LISTED_CATEGORY_LIMIT : FREE_LISTED_CATEGORY_LIMIT) - 1;
}

/**
 * The secondary categories the PUBLIC sees: the first one on a free listing,
 * all of them (up to 3) on a paid one.
 *
 * Same projection-not-constraint reasoning as `publicPackages` — the pro keeps
 * every category they authored, and upgrading restores them instantly.
 *
 * ⚠ FOUR CONSUMERS MUST AGREE: (1) display, here; (2) the search MATCH, which
 * reads the generated `listed_categories` column (`categoryMatchOr`); (3) the
 * `get_suggestion_counts` RPC, which otherwise inflates a category badge with
 * pros who are not publicly listed under it; (4) the sitemap's category count.
 * The column's SQL and this function must say the same thing.
 */
export function publicSecondaryCategories(
  plan: string | null | undefined,
  categories: readonly string[] | null | undefined,
): string[] {
  return categories ? categories.slice(0, secondaryCategoryLimit(plan)) : [];
}

/**
 * PostgREST `.or()` group matching listings PUBLISHED under any of these
 * categories: primary, or a secondary the plan publishes.
 *
 * Reads `listed_categories`, a generated column (migration
 * 20260919162224), because "the first secondary, on free rows only" cannot be
 * written as a PostgREST filter. Written once because the same expression is
 * needed at every category query site.
 */
export function categoryMatchOr(categories: readonly string[]): string {
  const arr = categories.map((c) => `"${c}"`).join(",");
  return `listed_categories.ov.{${arr}}`;
}

/**
 * How long a lapsed paid listing stays up after its paid period ends, so a late
 * or failed payment does not instantly cost the pro business.
 *
 * ⛔ THE GRACE WINDOW LIVES IN THE CRON, NOT HERE (moved 2026-09-02). It used to
 * be an app-layer filter — and that filter could never run, because the RLS read
 * policy on `professionals` admits `active|trial` only. The moment the cron set
 * `past_due` the database hid the row, so every app-side "within grace" branch
 * was filtering rows Postgres had already removed. Measured: a listing one day
 * into its ten-day grace read 0 rows as anon, 0 as another signed-in user, 1 as
 * its owner. Ten days of promised visibility, none of it real; the pro dashboard
 * meanwhile reported LISTING: Active, which is the confusing state the open
 * ticket described — two layers disagreeing.
 *
 * So `api/cron/subscription-expiry` now holds the transition back until
 * `subscription_expires_at + PAST_DUE_GRACE_DAYS`. The listing stays `active`
 * and genuinely visible for the whole window and then hides — the promise, kept
 * by the one component that owns the state change. Everything downstream gets
 * simpler: `past_due` now means "grace is over", full stop, so it is hard-gated
 * like the other two and there is no date arithmetic on any read path.
 *
 * The cost is that a row reads `active` for up to ten days after its paid period
 * ended, which is why `expiryGraceEndsAt` exists — the pro dashboard and the
 * admin list both need to say so out loud rather than showing "Renews on"
 * against a date in the past.
 */
export const PAST_DUE_GRACE_DAYS = 10;

const GRACE_MS = PAST_DUE_GRACE_DAYS * 24 * 60 * 60 * 1000;

/** Milliseconds of grace — for the cron, which is now the only place it applies. */
export const PAST_DUE_GRACE_MS = GRACE_MS;

/**
 * Statuses gated from public listings the moment they're set.
 *
 * `past_due` joined them when the grace moved to the cron: reaching this status
 * now MEANS the grace is spent. Note this matches the RLS read policy, which
 * admits `active|trial` — the app filter is belt, the policy is the boundary.
 */
export const HARD_GATED_STATUSES = ["past_due", "cancelled", "suspended"] as const;

/** PostgREST `not in (...)` value for the always-gated statuses. */
export const HARD_GATED_NOT_IN = `(${HARD_GATED_STATUSES.join(",")})`;

/** Single-row predicate for the slug page (owner check is applied separately). */
export function isListingPublic(p: {
  visible: boolean | null;
  subscription_status: string | null;
  subscription_expires_at: string | null;
}): boolean {
  if (!p.visible) return false;
  return !(HARD_GATED_STATUSES as readonly string[]).includes(p.subscription_status ?? "");
}

/** Why a listing is gated — drives the paused-page / banner copy. */
export type GateReason = "cancelled" | "suspended" | "past_due" | null;

export function gateReason(p: {
  subscription_status: string | null;
  subscription_expires_at: string | null;
}): GateReason {
  const s = p.subscription_status;
  if (s === "cancelled" || s === "suspended" || s === "past_due") return s;
  return null;
}

/**
 * When a listing whose paid period has ENDED but which is still live actually
 * comes down — or null if it is not in that window.
 *
 * This is the visible face of the cron-side grace. During the window the row is
 * still `active` with an expiry date in the PAST, which without this reads as
 * "Renews on <a date last week>" — a worse lie than the one this change fixes.
 * Callers use a non-null result for both jobs: suppress the renews-on line, and
 * say when the listing hides.
 *
 * ⚠ Call this on the SERVER and pass the result down. It needs `now`, and the
 * pro dashboard deliberately has no clock — comparing to `Date.now()` during a
 * client render reintroduces the hydration seam `useNow()` was removed to avoid.
 *
 * ⛔ THE PREDICATE MUST MIRROR THE CRON'S, INCLUDING ITS EXCLUSIONS. This is a
 * PROMISE ABOUT WHAT THE CRON WILL DO, so any row the cron skips must return
 * null here or the dashboard threatens something that never arrives. The cron
 * carries `.neq("plan","free")` and `.eq("founder", false)`; the first version
 * of this function checked only the status and the date, so a free listing with
 * an expiry date — production already has one — would have shown "your listing
 * is hidden on <date>" permanently, the date receding into the past while
 * nothing happened, and a lifetime founder with a stale expiry would have been
 * told the same. Found in review, not by a test, which is why the shared shape
 * below now takes the plan and the founder flag rather than letting a caller
 * forget them.
 *
 * No upper bound on purpose: if the cron is late the returned date is in the
 * recent past, which is true and says the listing is about to go. Returning null
 * there would put the dashboard back to showing a stale "Renews on" instead.
 */
export function expiryGraceEndsAt(
  p: {
    subscription_status: string | null;
    subscription_expires_at: string | null;
    /** Free never lapses — the cron excludes it, so there is no window to show. */
    plan: string | null;
    /** Founding members have lifetime access; the cron never expires them. */
    founder: boolean | null;
  },
  now: number = Date.now(),
): string | null {
  const s = p.subscription_status;
  if (s !== "active" && s !== "trial") return null;
  if (!isPaidPlan(p.plan) || p.founder) return null; // the cron would never hide it
  if (!p.subscription_expires_at) return null; // nothing to lapse
  const expired = new Date(p.subscription_expires_at).getTime();
  if (Number.isNaN(expired) || expired > now) return null; // still inside the paid period
  return new Date(expired + GRACE_MS).toISOString();
}
